// RegionMoveOverlay — 範囲の移動・コピーの「見た目」(docs/_wip/region-move-2026-10-15.md「UI」)。
//
//   - 範囲 (ドラッグ中 / 選択後): 点線の枠。
//   - 浮かせている間: 写し取った絵 (手元だけ。送らない)・置き先の枠・元の場所の点線
//     (移動のとき元の絵は engine が手元だけ隠す)・キャンバスの外にはみ出す部分 (確定すると切れる = 赤く示す)。
//   - 範囲 / 置き先の近くに `RegionActionCluster` (角に追従・はみ出したら反対側へ。
//     画像貼り付けの `computeOverlayAnchor` と同じ置き方で、実寸を測って位置決めする)。
//
// 座標は真の canvas 座標。画面への変換は `CanvasViewMapper` (zoom / pan / 反転 / 回転を 1 つの
// 変換で吸収) に任せ、絵は CSS `matrix()` で同じ変換に載せる。表示が変わる (pan / zoom / 回転は
// DOM イベントを出さない) ときは `onViewChange` で計算し直す。
//
// 浮かせた絵の表示濃度: **100% で描く** (レイヤーの表示濃度 / 👁 には従わない)。浮いているものが
// 「いま掴んでいる物」だと分かるのが優先で、👁 を切ったレイヤーから取っても絵が見えなくなる
// ことがないため。元の範囲を実際に隠すのは engine の仕事 (`setLocalRegionMask`) なので、ここでは点線だけ示す。

import { useEffect, useLayoutEffect, useReducer, useRef, useState } from 'react';

import {
  type CanvasSize,
  floatRect,
  type IntRect,
  outsideStrips,
} from '../../../application/regionMoveFlow.js';
import type { CanvasViewMapper } from '../../../infrastructure/drawing/viewport.js';
import type { RegionCapture, RegionMoveController } from '../../hooks/useRegionMove.js';
import { computeOverlayAnchor } from '../classic/ImagepasteOverlay.js';
import { RegionActionCluster, type RegionVariant } from './RegionMoveControls.js';

export interface RegionMoveOverlayProps {
  readonly viewMapper: CanvasViewMapper;
  /** 表示枠 (キャンバスが見える窓)。絵と点線はこの枠で切る。 */
  readonly frameRef: React.RefObject<HTMLElement | null>;
  readonly region: RegionMoveController;
  readonly canvas: CanvasSize;
  readonly variant: RegionVariant;
}

type Pt = { readonly x: number; readonly y: number };

/** 矩形の 4 隅を client 座標にする (回転しても歪まない)。 */
const cornersClient = (m: CanvasViewMapper, r: IntRect): [Pt, Pt, Pt, Pt] => [
  m.canvasToClient(r.x, r.y),
  m.canvasToClient(r.x + r.w, r.y),
  m.canvasToClient(r.x + r.w, r.y + r.h),
  m.canvasToClient(r.x, r.y + r.h),
];

/** 表示が変わるたびに再描画する (pan / zoom / 回転 / 枠のリサイズ)。 */
const useViewRedraw = (viewMapper: CanvasViewMapper, enabled: boolean): void => {
  const [, bump] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (!enabled) return;
    const off = viewMapper.onViewChange(bump);
    window.addEventListener('resize', bump);
    return () => {
      off();
      window.removeEventListener('resize', bump);
    };
  }, [viewMapper, enabled]);
};

/** 浮かせた絵 1 レイヤー分。putImageData のみ (描画ロジックは持たない)。 */
const FloatLayerCanvas = ({ capture }: { readonly capture: RegionCapture }): React.ReactNode => {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (el === null) return;
    const ctx = el.getContext('2d');
    if (ctx === null) return;
    ctx.putImageData(
      new ImageData(new Uint8ClampedArray(capture.rgba), capture.width, capture.height),
      0,
      0,
    );
  }, [capture]);
  return (
    <canvas
      ref={ref}
      width={capture.width}
      height={capture.height}
      data-testid={`region-float-layer-${capture.layerId}`}
      style={{ position: 'absolute', left: 0, top: 0, imageRendering: 'pixelated' }}
    />
  );
};

export const RegionMoveOverlay = ({
  viewMapper,
  frameRef,
  region,
  canvas,
  variant,
}: RegionMoveOverlayProps): React.ReactNode => {
  const { phase } = region;
  const visible = region.active && phase.kind !== 'idle';
  useViewRedraw(viewMapper, visible);

  const clusterRef = useRef<HTMLFieldSetElement | null>(null);
  const [clusterSize, setClusterSize] = useState<{ width: number; height: number } | null>(null);
  const clusterShown = visible && (phase.kind === 'selected' || phase.kind === 'floating');
  const sizeKey = `${phase.kind}:${phase.kind === 'floating' ? phase.srcLayerIds.length : 0}:${
    phase.kind === 'floating' ? phase.sending : false
  }`;
  // biome-ignore lint/correctness/useExhaustiveDependencies: 中身が変わるたびに実寸を測り直す
  useLayoutEffect(() => {
    if (!clusterShown) {
      setClusterSize(null);
      return;
    }
    const el = clusterRef.current;
    if (el === null) return;
    const r = el.getBoundingClientRect();
    setClusterSize((prev) =>
      prev !== null && prev.width === r.width && prev.height === r.height
        ? prev
        : { width: r.width, height: r.height },
    );
  }, [clusterShown, sizeKey, region.srcLayerIds.length, region.dstLayerId]);

  if (!visible) return null;

  const frame = frameRef.current?.getBoundingClientRect() ?? null;
  const ox = frame?.left ?? 0;
  const oy = frame?.top ?? 0;
  const local = (p: Pt): string => `${p.x - ox},${p.y - oy}`;
  const polygon = (r: IntRect): string => cornersClient(viewMapper, r).map(local).join(' ');

  const selecting = phase.kind === 'selecting' ? phase.rect : null;
  const selected = phase.kind === 'selected' ? phase.rect : null;
  const floating = phase.kind === 'floating' ? phase : null;
  const anchorRect: IntRect | null =
    floating !== null ? floatRect(floating.src, floating.dst) : selected;

  const strips = floating !== null && anchorRect !== null ? outsideStrips(anchorRect, canvas) : [];

  // ボタン群の位置 (画像貼り付けと同じ: 右上の外、溢れたら反対側へ、最後は viewport 内へ clamp)。
  let anchor: { left: number; top: number } | null = null;
  if (clusterShown && anchorRect !== null && typeof window !== 'undefined') {
    anchor = computeOverlayAnchor({
      previewBounds: viewMapper.canvasRectToClientBounds(
        anchorRect.x,
        anchorRect.y,
        anchorRect.w,
        anchorRect.h,
      ),
      viewport: { width: window.innerWidth, height: window.innerHeight },
      ...(clusterSize !== null ? { buttonsSize: clusterSize } : {}),
    });
  }

  const dashed = (
    r: IntRect,
    key: string,
    opts?: { dim?: boolean; solid?: boolean },
  ): React.ReactNode => (
    <g key={key}>
      <polygon
        points={polygon(r)}
        fill="none"
        stroke="#fff"
        strokeWidth={opts?.solid === true ? 3 : 2}
        opacity={opts?.dim === true ? 0.7 : 1}
      />
      <polygon
        points={polygon(r)}
        fill="none"
        stroke={opts?.solid === true ? '#2563eb' : '#000'}
        strokeWidth={1}
        strokeDasharray={opts?.solid === true ? undefined : '5 4'}
      />
    </g>
  );

  // 浮かせた絵を載せる変換 (canvas 座標 → client 座標の 1 次変換を CSS matrix に写す)。
  let floatMatrix: string | null = null;
  if (floating !== null) {
    const fr = floatRect(floating.src, floating.dst);
    const p00 = viewMapper.canvasToClient(fr.x, fr.y);
    const p10 = viewMapper.canvasToClient(fr.x + fr.w, fr.y);
    const p01 = viewMapper.canvasToClient(fr.x, fr.y + fr.h);
    const a = (p10.x - p00.x) / fr.w;
    const b = (p10.y - p00.y) / fr.w;
    const c = (p01.x - p00.x) / fr.h;
    const d = (p01.y - p00.y) / fr.h;
    floatMatrix = `matrix(${a},${b},${c},${d},${p00.x - ox},${p00.y - oy})`;
  }

  return (
    <>
      {/* 表示枠で切る描画層 (操作は奪わない)。 */}
      <div
        data-testid="region-overlay"
        aria-hidden="true"
        style={{
          position: 'fixed',
          left: ox,
          top: oy,
          width: frame?.width ?? 0,
          height: frame?.height ?? 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          zIndex: 30,
        }}
      >
        {floating !== null && floatMatrix !== null ? (
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: floating.src.w,
              height: floating.src.h,
              transformOrigin: '0 0',
              transform: floatMatrix,
              opacity: floating.sending ? 0.6 : 1,
            }}
          >
            {floating.captures.map((cap) => (
              <FloatLayerCanvas key={cap.layerId} capture={cap} />
            ))}
          </div>
        ) : null}
        <svg
          aria-hidden="true"
          width={frame?.width ?? 0}
          height={frame?.height ?? 0}
          style={{ position: 'absolute', left: 0, top: 0, overflow: 'visible' }}
        >
          {selecting !== null ? dashed(selecting, 'selecting') : null}
          {selected !== null ? dashed(selected, 'selected') : null}
          {floating !== null ? (
            <>
              {dashed(floating.src, 'src', { dim: true })}
              {strips.map((s) => (
                <polygon
                  key={`o-${s.x}-${s.y}-${s.w}-${s.h}`}
                  points={polygon(s)}
                  fill="rgba(239,68,68,0.35)"
                  data-testid="region-outside-strip"
                />
              ))}
              {anchorRect !== null ? dashed(anchorRect, 'dst', { solid: true }) : null}
            </>
          ) : null}
        </svg>
      </div>

      {clusterShown && anchor !== null ? (
        <fieldset
          ref={clusterRef}
          aria-label="範囲の移動・コピー"
          data-testid="region-cluster"
          // 出た瞬間にゴーストクリックを受けないよう phase ごとに作り直す。
          key={phase.kind}
          style={{
            position: 'fixed',
            left: anchor.left,
            top: anchor.top,
            maxWidth: 'calc(100vw - 16px)',
            zIndex: 40,
          }}
          className={`m-0 flex flex-wrap items-center gap-1.5 p-1 ${
            variant === 'classic'
              ? 'border border-[var(--color-border)] bg-[var(--color-bg-soft)]'
              : 'rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)]/95 shadow-lg'
          }`}
        >
          <RegionActionCluster region={region} variant={variant} hasOverflow={strips.length > 0} />
        </fieldset>
      ) : null}
    </>
  );
};
